import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult } from "@tanstack/react-query";

import { getPrivateInstance } from "../_privateInstance";
import { parseStaffRegulator } from "./parseStaffRegulator";
import { staffRegulatorApprovePath } from "./staffRegulatorPaths";
import { STAFF_REGULATORS_LIST_QUERY_KEY } from "./useStaffRegulatorsList";
import type { StaffRegulator, StaffRegulatorEnvelope } from "./types";

export const postApproveStaffRegulator = async (
  adminId: string,
): Promise<StaffRegulator> => {
  const response = await getPrivateInstance().post<
    StaffRegulatorEnvelope<unknown>
  >(staffRegulatorApprovePath(adminId), {});
  const parsed = parseStaffRegulator(response.data?.data);
  if (!parsed) {
    // Approve is allowed to return an empty 200 envelope; treat that as
    // success rather than leaving the mutation without a record.
    return {
      admin_id: adminId,
      email: "",
      status: "approved",
      given_name: null,
      family_name: null,
      timezone: null,
      whitelisted_ips: [],
    };
  }
  return parsed;
};

export const useApproveStaffRegulator = (): UseMutationResult<
  StaffRegulator,
  Error,
  string
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["staff-regulators", "approve"],
    mutationFn: postApproveStaffRegulator,
    onSuccess: (_data, adminId) => {
      queryClient.invalidateQueries({
        queryKey: ["staff-regulators", "detail", adminId],
      });
      queryClient.invalidateQueries({
        queryKey: STAFF_REGULATORS_LIST_QUERY_KEY,
      });
    },
  });
};
